/**
 * shared/i18n — internacionalização em pt-BR (DD-007, RNF 13).
 *
 * Ponto central de todas as strings visíveis do portal e dos formatadores
 * `Intl` de número e data. Identificadores em inglês; textos em pt-BR.
 *
 * Funções puras: este módulo não importa React.
 */

/** Dicionário central de strings pt-BR. Chave em inglês, valor em pt-BR. */
export const dictionary: Record<string, string> = {
  // Ações comuns
  "common.save": "Salvar",
  "common.cancel": "Cancelar",
  "common.confirm": "Confirmar",
  "common.delete": "Excluir",
  "common.edit": "Editar",
  "common.retry": "Tentar novamente",
  "common.loading": "Carregando…",
  "common.empty": "Sem dados no período",
  "common.search": "Buscar",
  "common.close": "Fechar",
  // Autenticação
  "auth.login": "Entrar",
  "auth.logout": "Sair",
  "auth.email": "E-mail corporativo",
  "auth.password": "Senha",
  // Navegação (Lista Canônica 4.1)
  "nav.dashboard": "Visão geral",
  "nav.pipeline": "Pipeline",
  "nav.opportunity": "Oportunidade",
  "nav.partners": "Parcerias",
  "nav.accounts": "Contas",
  "nav.activities": "Atividades",
  "nav.inbox": "Caixa de entrada",
  "nav.reports": "Relatórios",
  "nav.settings": "Configurações",
  "nav.admin": "Administração",
  "nav.platform": "Plataforma",
  "nav.import": "Importação legada",
  "nav.audit": "Auditoria",
  "nav.digest": "Digest diário",
  // Erros (catálogo WEB-ERR-*)
  "error.WEB-ERR-001": "E-mail ou senha inválidos.",
  "error.WEB-ERR-002": "Sua sessão expirou. Entre novamente.",
  "error.WEB-ERR-003": "Você não tem acesso a este recurso.",
  "error.WEB-ERR-004": "Convite inválido ou expirado.",
  "error.WEB-ERR-005": "As senhas não coincidem.",
  "error.WEB-ERR-006": "Senha muito fraca.",
  "error.WEB-ERR-007": "Não foi possível salvar a mudança de estágio.",
  "error.WEB-ERR-008": "Dados obrigatórios para avançar de estágio.",
  "error.WEB-ERR-009": "Serviço temporariamente indisponível.",
  "error.WEB-ERR-010": "Sem conexão com o servidor.",
  "error.WEB-ERR-011": "Recurso ainda não disponível.",
  "error.WEB-ERR-012": "Não foi possível exportar o relatório.",
  "error.WEB-ERR-013": "Verifique os campos destacados.",
  // 404: `mapStatusToErrorCode` manda 404 para WEB-ERR-011 ("Recurso ainda não
  // disponível"), que lê como funcionalidade inacabada. Item inexistente — ou que o
  // tenant não enxerga — precisa de frase própria.
  "error.WEB-ERR-014": "O item não foi encontrado ou você não tem acesso a ele.",
};

/**
 * Resolve uma chave do dicionário pt-BR. Quando ausente, retorna a própria
 * chave (fallback visível que sinaliza string não traduzida em desenvolvimento).
 */
export function t(key: string): string {
  return dictionary[key] ?? key;
}

const FALLBACK_LOCALE = "pt-BR";

/** Formata número em pt-BR (separador de milhar `.`, decimal `,`). */
export function formatNumber(
  value: number,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(FALLBACK_LOCALE, options).format(value);
}

/** Formata data (Date | ISO string | epoch ms) em pt-BR `dd/mm/aaaa` (UTC). */
export function formatDate(
  value: Date | string | number,
  options?: Intl.DateTimeFormatOptions,
): string {
  const date = value instanceof Date ? value : new Date(value);
  return new Intl.DateTimeFormat(FALLBACK_LOCALE, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
    ...options,
  }).format(date);
}
